import React, { useEffect, useMemo, useState } from 'react';
import { Form, Image, Space } from 'antd';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { addPages, removePage } from '../../../redux/reducers/navigationReducer/NavigationSlice';
import PageHeader from '../../../components/common/pageHeader/PageHeader';
import { PageContainer, PrimaryButton, SpaceEnd } from '../../../styles/common.styles';
import { NavigationKey } from '../../../components/appNavigationBar/AppNavigation.types';
import {
    deleteLiveGuidelineAction,
    getLiveGuidelinesListAction,
} from '../../../redux/actions/liveGuidelineActions';
import { LiveGuideline } from '../../../redux/actions/liveGuidelineActions/types';
import { ActionResponse } from '../../../models/actionResponse/types';
import { columns } from './LiveGuidelinesList.columns';
import CreateLiveGuidelineModal from '../../../components/liveGuidelines/createLiveGuidelineModal/CreateLiveGuidelineModal';
import { useModalState } from '../../../hooks/modalState';
import { usePermissions } from '../../../hooks/permissions';
import EmptyData from '../../../components/common/emptyData/EmptyData';
import {
    GuideLineTable,
    LiveGuidelinesListControls,
    LiveGuidelinesListContainer,
    LiveGuidelinesListHeader,
    SearchInput,
    TableCount,
    UnselectAllButton,
} from './LiveGuidelinesList.styles';
import NotificationModal from '../LiveGuidelineDetail/tabs/modals/NotificationModal';
import DeleteModal from '../../../components/common/deleteModal/DeleteModal';
import ChangePageSize from '../../../components/common/pagination/ChangePageSize';
import LiveGuidelineExportModal from './LiveGuidelineExportModal';
import CustomPagination from '../../../components/common/pagination/CustomPagination';

// eslint-disable-next-line max-lines-per-function
const LiveGuidelinesList: React.FC = () => {
    const dispatch = useAppDispatch();
    const selector = useAppSelector((state) => state);
    const { authReducer } = selector;
    const { canManageGuidelines } = usePermissions();
    const createModal = useModalState();
    const notificationModal = useModalState();
    const deleteModal = useModalState();
    const exportModal = useModalState();
    const [selectedGuidelineId, setSelectedGuidelineId] = useState<number | null>(null);
    const [guidelineToExport, setGuidelineToExport] = useState<LiveGuideline | null>(null);
    const [guidelineToDelete, setGuidelineToDelete] = useState<LiveGuideline | null>(null);
    const [deletePending, setDeletePending] = useState(false);
    const [searchString, setSearchString] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

    const [state, setState] = useState<{ pending: boolean; guidelines: LiveGuideline[] }>({
        pending: true,
        guidelines: [],
    });

    useEffect(() => {
        dispatch(
            addPages([
                {
                    key: NavigationKey.DASHBOARD,
                    value: `${authReducer.user?.firstName}’s Dashboard`,
                },
                {
                    key: NavigationKey.LIVE_GUIDELINES,
                    value: 'Live Guidelines',
                    image: 'src/assets/images/lightning-charge-fill.svg',
                },
            ]),
        );
        getGuidelines();

        return () => {
            dispatch(removePage());
        };
    }, []);

    const getGuidelines = async () => {
        // Backend GetList returns a plain array (no total count) - fetch a large page
        // and let the table paginate client-side.
        const { payload } = (await dispatch(
            getLiveGuidelinesListAction({ curPage: 1, pageSize: 1000 }),
        )) as ActionResponse<LiveGuideline[]>;
        setState({ pending: false, guidelines: payload?.data || [] });
    };

    const filteredGuidelines = useMemo(() => {
        const query = searchString.trim().toLowerCase();
        return state.guidelines.filter(
            (guideline) => !query || guideline.name.toLowerCase().includes(query),
        );
    }, [searchString, state.guidelines]);

    const paginatedGuidelines = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filteredGuidelines.slice(start, start + pageSize);
    }, [filteredGuidelines, page, pageSize]);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchString(event.target.value);
        setPage(1);
    };

    const clearSearchInput = () => {
        setSearchString('');
        setPage(1);
    };

    const handleSearchSubmit = () => setPage(1);

    const handlePageChange = (nextPage: number) => setPage(nextPage);

    const handleCurrentPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        const nextPage = Number(event.target.value);
        const totalPages = Math.max(1, Math.ceil(filteredGuidelines.length / pageSize));
        if (nextPage >= 1 && nextPage <= totalPages) setPage(nextPage);
    };

    const handlePageSizeChange = (nextPageSize: number) => {
        setPageSize(nextPageSize);
        setPage(1);
    };

    const openNotificationModal = (guideline: LiveGuideline) => {
        setSelectedGuidelineId(guideline.id);
        notificationModal.onOpen();
    };

    const closeNotificationModal = () => {
        notificationModal.onClose();
        setSelectedGuidelineId(null);
    };

    const openExportModal = (guideline: LiveGuideline) => {
        setGuidelineToExport(guideline);
        exportModal.onOpen();
    };

    const closeExportModal = () => {
        exportModal.onClose();
        setGuidelineToExport(null);
    };

    const openDeleteModal = (guideline: LiveGuideline) => {
        setGuidelineToDelete(guideline);
        deleteModal.onOpen();
    };

    const closeDeleteModal = () => {
        deleteModal.onClose();
        setGuidelineToDelete(null);
    };

    const onDeleteGuideline = async () => {
        if (!guidelineToDelete) {
            return;
        }

        setDeletePending(true);
        try {
            const result = await dispatch(deleteLiveGuidelineAction(guidelineToDelete.id));
            if (deleteLiveGuidelineAction.fulfilled.match(result)) {
                setState((previous) => ({
                    ...previous,
                    guidelines: previous.guidelines.filter(
                        (guideline) => guideline.id !== guidelineToDelete.id,
                    ),
                }));
                setSelectedRowKeys((previous) =>
                    previous.filter((key) => key !== guidelineToDelete.id),
                );
                closeDeleteModal();
            }
        } finally {
            setDeletePending(false);
        }
    };
    return (
        <>
            <PageHeader
                title="Live Guidelines"
                iconTitleGap={4}
                iconProp={{ src: 'lightning-charge-fill.svg', style: { width: 24, height: 24 } }}
            />
            <PageContainer>
                <LiveGuidelinesListContainer>
                    <LiveGuidelinesListHeader>
                        {canManageGuidelines() && (
                            <PrimaryButton onClick={createModal.onOpen}>
                                Create Live Guideline
                            </PrimaryButton>
                        )}
                    </LiveGuidelinesListHeader>
                    <LiveGuidelinesListControls>
                        <div>
                            <Form>
                                <Space>
                                    <SearchInput
                                        placeholder="Live Guideline name"
                                        onPressEnter={handleSearchSubmit}
                                        onChange={handleSearchChange}
                                        value={searchString}
                                        id="live-guidelines-search-input"
                                        prefix={
                                            !searchString && (
                                                <Image
                                                    src="src/assets/images/magnifying-glass.svg"
                                                    preview={false}
                                                />
                                            )
                                        }
                                        suffix={
                                            searchString && (
                                                <Image
                                                    preview={false}
                                                    src="src/assets/images/close-circle-icon.svg"
                                                    onClick={clearSearchInput}
                                                />
                                            )
                                        }
                                    />
                                </Space>
                            </Form>
                            <TableCount>{filteredGuidelines.length} Live Guidelines</TableCount>
                            {canManageGuidelines() && (
                                <UnselectAllButton onClick={() => setSelectedRowKeys([])}>
                                    Unselect all
                                </UnselectAllButton>
                            )}
                        </div>
                        <SpaceEnd>
                            <ChangePageSize
                                changePageSize={handlePageSizeChange}
                                pageSize={pageSize}
                            />
                            <CustomPagination
                                page={page}
                                total={filteredGuidelines.length || 1}
                                pageSize={pageSize}
                                changePageNumber={handlePageChange}
                                handleCurrentPage={handleCurrentPage}
                            />
                        </SpaceEnd>
                    </LiveGuidelinesListControls>
                    <GuideLineTable
                        columns={
                            columns(
                                openNotificationModal,
                                openExportModal,
                                openDeleteModal,
                                canManageGuidelines(),
                            ) as any
                        }
                        dataSource={paginatedGuidelines}
                        rowKey="id"
                        rowSelection={
                            canManageGuidelines()
                                ? {
                                    selectedRowKeys,
                                    columnWidth: 16,
                                    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
                                }
                                : undefined
                        }
                        pagination={false}
                        loading={state.pending}
                        locale={{
                            emptyText: (
                                <EmptyData text="No Live Guidelines yet." pending={state.pending} />
                            ),
                        }}
                    />
                    <SpaceEnd>
                        <CustomPagination
                            page={page}
                            total={filteredGuidelines.length || 1}
                            pageSize={pageSize}
                            changePageNumber={handlePageChange}
                            handleCurrentPage={handleCurrentPage}
                        />
                    </SpaceEnd>
                </LiveGuidelinesListContainer>
            </PageContainer>
            {createModal.isOpen && (
                <CreateLiveGuidelineModal
                    isOpen={createModal.isOpen}
                    handleCancel={createModal.onClose}
                />
            )}

            {notificationModal.isOpen && selectedGuidelineId !== null && (
                <NotificationModal
                    isOpen={notificationModal.isOpen}
                    handleCancel={closeNotificationModal}
                    onSuccess={closeNotificationModal}
                    liveGuidelineId={selectedGuidelineId}
                />
            )}
            {guidelineToDelete && (
                <DeleteModal
                    isOpen={deleteModal.isOpen}
                    closeModal={closeDeleteModal}
                    reload={onDeleteGuideline}
                    title="Live Guideline"
                    text={guidelineToDelete.name}
                    pending={deletePending}
                />
            )}
            {guidelineToExport && (
                <LiveGuidelineExportModal
                    guideline={guidelineToExport}
                    isOpen={exportModal.isOpen}
                    closeModal={closeExportModal}
                />
            )}
        </>
    );
};

export default LiveGuidelinesList;
